import { foldApostrophes } from "./scenario";

/**
 * TA-1355 D1, Kabir's typed "fix" of 2026-10-17. A customer who only thanks
 * the shop or confirms a visit ("perfect thanks, see you saturday") got the
 * fixed holding line, "Checking on that for you, back shortly.", which answers
 * nothing and promises a follow-up nobody owes. The turn now composes nothing
 * and is recorded as restraint (B3: silence is a first-class action).
 *
 * Silence on a real question is the worse failure, so the reader is narrow and
 * anything it does not know composes as before:
 * - a thank-you or farewell word is required ("ok", "perfect" and "sounds
 *   good" alone may be an answer to the shop);
 * - every other word is a closing word on the list below, so a named piece, a
 *   service, "yes", "please" or "i'll take it" composes;
 * - no "?", no digit ("see you at 3" is a time to confirm), at most 80 characters;
 * - the shop has spoken in the thread, and its last turn asked nothing: after
 *   "want me to hold it?", "ok thanks" is an answer.
 */
const THANKS_OR_FAREWELL = /\b(thanks|thank|thx|ty|tysm|appreciate|appreciated|cheers|bye|cya|see (you|u|ya))\b/;

const CLOSING_WORDS: ReadonlySet<string> = new Set([
  "thanks", "thank", "thx", "ty", "tysm", "appreciate", "appreciated", "cheers", "bye", "cya",
  "you", "u", "ya", "so", "much", "very", "a", "lot", "again", "for", "it", "that", "thats", "all",
  "the", "help", "info", "quick", "reply", "response", "ok", "okay", "k", "kk", "perfect", "great",
  "awesome", "cool", "nice", "amazing", "wonderful", "lovely", "sounds", "good", "will", "do", "see",
  "then", "soon", "there", "later", "tomorrow", "today", "tonight", "monday", "tuesday", "wednesday",
  "thursday", "friday", "saturday", "sunday", "weekend", "morning", "afternoon", "evening", "have",
  "day", "night", "and", "lol", "haha", "talk", "man", "sir", "guys", "love", "x", "xx",
]);

export const ACKNOWLEDGEMENT_MAX_CHARS = 80;

export function isPureAcknowledgement(text: string, priorShopTurns: readonly string[]): boolean {
  const lastShop = priorShopTurns[priorShopTurns.length - 1];
  if (lastShop === undefined || lastShop.includes("?")) return false;
  const folded = foldApostrophes(text).toLowerCase().trim();
  if (folded.length === 0 || folded.length > ACKNOWLEDGEMENT_MAX_CHARS) return false;
  if (folded.includes("?") || /\d/.test(folded)) return false;
  const words = folded.replace(/'/g, "").split(/[^a-z]+/).filter((w) => w.length > 0);
  if (words.length === 0 || !THANKS_OR_FAREWELL.test(words.join(" "))) return false;
  return words.every((w) => CLOSING_WORDS.has(w));
}
